import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";

import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import CommonTable from "../../../components/Common/TableContainer";
import JobCategoryAddEditModal from "pages/AllCommonModal/JobCategoryAddEditModal/job-category-add-edit-modal";
import { submitFilterData } from "model/CategoryModel";
import Spinners from "components/Common/Spinner";
import { useJobCategoryContext } from "libs/context/JobCategoryContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { pascalToCamel } from "helpers/GeneralHelper";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";

const JobCategory = () => {
    const { permissionData } = usePermissionContext() || {};
    const { fetchJobCategoryContextData, jobCategoryContextData, jobCategoryVersion } = useJobCategoryContext() || {};
    const [jobCategoryAddEditModalOpen, setJobCategoryAddEditModalOpen] = useState(false);
    const [modal1, setModal1] = useState(false);
    const toggleViewModal = () => setModal1(!modal1);
    const [searchQuery, setSearchQuery] = useState("");
    const [jobCategoryData, setJobCategoryData] = useState("");
    const [loading, setLoading] = useState(false);
    const [itemId, setItemId] = useState(null); // ✅ important
    const initialState = {
        name: "",
        incentive: "",
        recruiterIncentiveAmount: "",
        backendIncentiveAmount: "",
        vTitle: "",
        vLink: "",
        quota: false,
        status: true,
    };

    const [formData, setFormData] = useState(initialState);
    const [tableMetaData, setTableMetadata] = useState();
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
    const [versionFromList, setVersionFromList] = useState();


    const filteredData = useMemo(() => {
        if (!searchQuery?.trim()) return jobCategoryData;

        return jobCategoryData?.filter((item) =>
            Object.values(item).some((val) =>
                String(val).toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [jobCategoryData]);

    const jobCategoryActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.JobCategoryAddAccess),
            canEdit: Boolean(permissionData?.JobCategoryUpdateAccess),
        }),
        [permissionData]
    );

    const hasAnyJobCategoryRowAction = useMemo(
        () => jobCategoryActionPermissions.canEdit,
        [jobCategoryActionPermissions]
    );

    // ✅ FIXED: set itemId FIRST, then open modal
    const handleEdit = (row) => {
        setItemId(row?.id);
        setJobCategoryAddEditModalOpen(true);
    };

    useEffect(() => {
        fetchJobCategoryContextData()
        getJobCategoryList();
    }, []);


    useEffect(() => {
        const valueReModified = jobCategoryContextData?.table?.columns?.map(col => ({
            Header: col?.title,
            accessor: col?.field,
        }));
        setTableMetadata(valueReModified)
    }, [jobCategoryContextData]);

    const getJobCategoryList = async () => {
        setLoading(true);
        let response = await submitFilterData({});
        let categoryData = response?.data;
        const valueReModified = categoryData?.map(item => ({
            ...item,
            ID: item?.Id,
        }));
        if (response) {
            setJobCategoryData(valueReModified);
            setVersionFromList(response?.schemaVersion);
            setLoading(false);
        } else {
            setJobCategoryData([]);
            setLoading(false);
        }
    };

    const handleFilterSubmit = async (data) => {
        setLoading(true);
        let response = await submitFilterData(data);
        let filterData = response?.data;

        if (response) {
            setJobCategoryData(filterData);
            setLoading(false);
            setCommonFilterModalOpen(false);
        } else {
            setJobCategoryData([]);
            setLoading(false);
            setCommonFilterModalOpen(false);
        }
    };

    const handleSearchSubmit = async (value) => {
        setLoading(true);

        const payload = {
            search: value, // adjust key if backend expects something else
        };

        let response = await submitFilterData(payload);
        let searchData = response?.data;

        if (response) {
            setJobCategoryData(searchData);
        } else {
            setJobCategoryData([]);
        }

        setLoading(false);
    };


    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
                    {/* Table */}
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">Job Category</h5>

                                {jobCategoryActionPermissions.canCreate ? (
                                    <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                setItemId(null);
                                                setJobCategoryAddEditModalOpen(true);
                                                setFormData(initialState);
                                            }}
                                        >
                                            + Create
                                        </Button>
                                    </Col>
                                ) : null}
                            </div>
                            <hr />

                            <Row className="align-items-center g-2 mb-4">
                                <Col xs="12" md="8">
                                    <div className="d-flex border rounded w-100 category-search-input">
                                        <Input
                                            type="text"
                                            placeholder="Search by name"
                                            className="border-0"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e?.target?.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleSearchSubmit(searchQuery?.trim());
                                                }
                                            }}
                                        />

                                        <Button
                                            className="search-icon-button"
                                            onClick={() => handleSearchSubmit(searchQuery?.trim())}
                                        >
                                            <i className="fa fa-search"></i>
                                        </Button>
                                    </div>
                                </Col>

                            </Row>

                            {/* <hr /> */}
                            {/* <div className="mb-4 h4 card-title">Job Category</div> */}
                            {(!loading && jobCategoryData?.length && tableMetaData?.length) ? (
                                <CommonTable
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyJobCategoryRowAction}
                                    onEdit={
                                        jobCategoryActionPermissions.canEdit ? handleEdit : undefined
                                    }
                                    isEditAccess={jobCategoryActionPermissions.canEdit}
                                    isCheckboxLeftSide={false}
                                    isPagination={false}
                                    setCommonFilterModalOpen={setCommonFilterModalOpen}
                                    pageName={"Job Category"}
                                />
                            ) : 
                            // <Spinners setLoading={setLoading} />
                            <Loaders />
                            }
                        </CardBody>
                    </Card>

                    {/* Add/Edit Modal */}
                    <JobCategoryAddEditModal
                        setJobCategoryAddEditModalOpen={setJobCategoryAddEditModalOpen}
                        jobCategoryAddEditModalOpen={jobCategoryAddEditModalOpen}
                        getJobCategoryList={getJobCategoryList}
                        itemId={itemId}
                        setJobCategoryData={setJobCategoryData}
                        setFormData={setFormData}
                        formData={formData}
                        totalData={jobCategoryData}
                    />
                    <AllCommonFilterModal
                        setCommonFilterModalOpen={setCommonFilterModalOpen}
                        commonFilterModalOpen={commonFilterModalOpen}
                        modalTitle="Job Category"
                        handleSubmit={handleFilterSubmit}
                        filterData={jobCategoryContextData?.filters}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

JobCategory.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(JobCategory);
